import { api } from "@/lib/api";
import type {
  HermesHtmlPreviewDetail,
  HermesHtmlPreviewSummary,
} from "@/lib/hermes-html-preview";

export type { HermesHtmlPreviewDetail, HermesHtmlPreviewSummary };

/** 限制聊天预览详情并发，避免多卡同时展开 Liquid 占满浏览器连接与 Puma 线程。 */
const HTML_PREVIEW_FETCH_CONCURRENCY = 2;
let htmlPreviewFetchesActive = 0;
const htmlPreviewFetchWaiters: Array<() => void> = [];

async function withHtmlPreviewFetchLimit<T>(task: () => Promise<T>): Promise<T> {
  if (htmlPreviewFetchesActive >= HTML_PREVIEW_FETCH_CONCURRENCY) {
    await new Promise<void>((resolve) => {
      htmlPreviewFetchWaiters.push(resolve);
    });
  }
  htmlPreviewFetchesActive += 1;
  try {
    return await task();
  } finally {
    htmlPreviewFetchesActive -= 1;
    htmlPreviewFetchWaiters.shift()?.();
  }
}

export type HermesWriteProposal = {
  id: number;
  action: string;
  payload: Record<string, unknown>;
  status: string;
  before_text?: string | null;
  after_text?: string | null;
  hermes_message_id?: number | null;
  created_at: string;
  updated_at: string;
};

/** Write proposals attached to an assistant message tool_payload for confirm cards. */
export type HermesMessageProposal = {
  id: number;
  action?: string;
  payload?: Record<string, unknown> | null;
  /** Live DB status when present (tool_payload snapshot may omit this). */
  status?: "pending" | "accepted" | "rejected";
};

export type HermesProposalDecision = "pending" | "accepted" | "rejected";

export function coerceProposalStatus(value: unknown): HermesProposalDecision | null {
  if (value === "pending" || value === "accepted" || value === "rejected") return value;
  return null;
}

export type HermesMessage = {
  id: number;
  role: string;
  content: string;
  client_id?: string | null;
  tool_name?: string | null;
  tool_payload?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

export type HermesChat = {
  id: number;
  logical_path: string;
  skills: string[];
  context?: Record<string, unknown>;
  /** Persisted session title; preferred over preview in the session list. */
  title?: string | null;
  /** First user message snippet from list endpoint; omitted on create/show. */
  preview?: string | null;
  created_at: string;
  updated_at: string;
};

export type KbWikiAccess = "read" | "write";

export type KbWikiBinding = {
  wiki_id: number;
  access: KbWikiAccess;
};

/** 从会话 context 中读取并清洗 Wiki 绑定；忽略非法或重复项。 */
export function parseKbBindingsFromContext(
  context: Record<string, unknown> | null | undefined,
): KbWikiBinding[] {
  const raw = context?.kb_bindings;
  if (!Array.isArray(raw)) return [];

  const bindings = new Map<number, KbWikiBinding>();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const wikiId = Number(record.wiki_id);
    const access = record.access;
    if (!Number.isInteger(wikiId) || wikiId <= 0) continue;
    if (access !== "read" && access !== "write") continue;
    bindings.set(wikiId, { wiki_id: wikiId, access });
  }
  return Array.from(bindings.values());
}

/** 绑定中只读 / 完全控制数量（chip 用图标 + 数字展示）。 */
export function countKbBindings(bindings: KbWikiBinding[]): {
  readable: number;
  writable: number;
  total: number;
} {
  const writable = bindings.filter((binding) => binding.access === "write").length;
  const readable = bindings.length - writable;
  return { readable, writable, total: bindings.length };
}

/** 知识库 chip 的无障碍文案（完整说明；可见 UI 用图标）。 */
export function summarizeKbBindings(bindings: KbWikiBinding[]): string {
  if (bindings.length === 0) return "知识库";

  const { readable, writable } = countKbBindings(bindings);
  if (bindings.length === 1) {
    return writable === 1 ? "知识库 · 完全控制" : "知识库 · 只读";
  }
  if (readable === 0) return `知识库 · ${writable} 个完全控制`;
  if (writable === 0) return `知识库 · ${readable} 个只读`;
  return `知识库 · ${readable} 只读 / ${writable} 完全控制`;
}

export type StreamEvent = {
  type: string;
  /** 子工作流进度，如 karpathy_wiki: started / done / error。 */
  status?: string;
  message_id?: number;
  /** User turn that owns this stream event (cancel / delete / deltas). */
  user_message_id?: number;
  delta?: string;
  /** tool_call 安全字段：官网页 / 栏目等目标（服务端白名单，勿传 html） */
  slug?: string;
  key?: string;
  path?: string;
  query?: string;
  public_id?: string;
  preview_id?: string;
  collection_key?: string;
  /** Present on webfetch `tool_result` stream events */
  bytes?: number | null;
  truncated?: boolean;
  final_url?: string | null;
  error?: string | null;
  /**
   * Future: next-turn conversation starters from the model / server on `done`.
   * Empty-state suggestions are client-side today; this field is the reply-time hook.
   * Clients may ignore until product wires UI.
   */
  suggestions?: string[];
  /** Session title on `chat_updated` / optionally mirrored on `done`. */
  title?: string;
};

export type HermesToolResultMeta = {
  name: string;
  bytes?: number | null;
  truncated?: boolean;
  final_url?: string | null;
  error?: string | null;
};

export type HermesScoreOptimizePlanItem = {
  slug: string;
  action: string;
  expect?: string;
};

export type HermesScoreOptimizePlanCard = {
  status: "pending_confirmation";
  plan_id: string;
  plan: HermesScoreOptimizePlanItem[];
};

export type HermesScoreOptimizePlanState = {
  status: "planning" | "awaiting_confirm" | "executing";
  plan_id: string | null;
  plan: HermesScoreOptimizePlanItem[];
};

export type HermesStreamProgress = {
  messages: HermesMessage[];
  /** Latest tool / status hint while the stream is still open */
  status?: string | null;
};

/** Cable push payload from HermesStreamChannel / StreamBroadcaster */
export type HermesCablePayload = {
  account_id: number;
  chat_id: number;
  event: StreamEvent;
  message?: HermesMessage | null;
};

export const HERMES_SKILLS = [
  { id: "kb", label: "知识库" },
  { id: "fetch", label: "联网抓取" },
] as const;

export type HermesSkillId = (typeof HERMES_SKILLS)[number]["id"];

/** 技能包内单文件引用（create / update upsert） */
export type HermesSkillFileRef = { path: string; body?: string };

/** 技能元数据（create / update / import 草稿） */
export type HermesSkillMeta = {
  name?: string;
  title?: string | null;
  description?: string | null;
  version?: string | null;
  source_url?: string | null;
  tags?: string[] | null;
};

/** import_url 返回的未落库草稿包 */
export type HermesSkillImportDraft = {
  meta: HermesSkillMeta & { name: string };
  files: Array<{ path: string; body: string }>;
  stripped_paths: string[];
  scan_verdict: string;
  saveable: boolean;
};

/** 系统技能相对 origin 快照的单文件 diff */
export type HermesSkillOriginDiffEntry = {
  path: string;
  before: string;
  after: string;
  unified: string;
};

export type HermesSkillCreatePayload = {
  target: HermesSkillInstallTarget;
  files: HermesSkillFileRef[];
  meta?: HermesSkillMeta;
};

export type HermesSkillPackageUpdatePayload = {
  enabled?: boolean;
  files_upsert?: HermesSkillFileRef[];
  files_delete?: string[];
  meta?: HermesSkillMeta;
};

/** DB 技能（Hermes 渐进披露 Skills；与 toolset chip 无关） */
export type HermesDbSkill = {
  id: number;
  name: string;
  title?: string | null;
  description?: string | null;
  visibility: "system" | "account" | "user";
  enabled: boolean;
  source_url?: string | null;
  tags?: string[] | null;
  version?: string | null;
  scan_verdict?: string | null;
  file_count?: number;
  created_at: string;
  updated_at: string;
};

export type HermesDbSkillDetail = HermesDbSkill & { files: { path: string }[] };

export type HermesSkillScope = "user" | "account" | "system" | "all";

export type HermesSkillInstallTarget = "user" | "account";

/** show_file 路由用：保留 `/` 分隔符，逐段 URL 编码 */
export function encodeHermesSkillFilePath(path: string): string {
  return path
    .split("/")
    .filter((segment) => segment.length > 0)
    .map(encodeURIComponent)
    .join("/");
}

/** 卡片来源文案：系统 / URL 域名 / 个人·企业 */
export function hermesDbSkillSourceLabel(skill: HermesDbSkill): string {
  if (skill.visibility === "system") return "系统";
  if (skill.source_url) {
    const host = fetchUrlHostLabel(skill.source_url);
    return host || "已安装";
  }
  return skill.visibility === "account" ? "企业" : "个人";
}

/** Stub policy: auto-reopen drawer on done while closed. Keep false until product enables. */
export const HERMES_AUTO_REOPEN_ON_DONE = false;

export function createHermesApi(accountId: string) {
  const chatsPath = "/api/v1/hermes/chats";
  const skillsPath = "/api/v1/hermes/skills";

  return {
    listChats(params?: { mode?: string; brand_site_id?: number }) {
      const sp = new URLSearchParams();
      if (params?.mode) sp.set("mode", params.mode);
      if (params?.brand_site_id != null) sp.set("brand_site_id", String(params.brand_site_id));
      const qs = sp.toString();
      return api.fetchForAccount<{ chats: HermesChat[] }>(
        accountId,
        qs ? `${chatsPath}?${qs}` : chatsPath,
      );
    },

    createChat(skills: string[], context?: Record<string, unknown>) {
      return api.fetchForAccount<{ chat: HermesChat }>(accountId, chatsPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ skills, ...(context ? { context } : {}) }),
      });
    },

    updateChat(
      chatId: number,
      payload: { skills?: string[]; context?: Record<string, unknown> },
    ) {
      return api.fetchForAccount<{ chat: HermesChat }>(
        accountId,
        `${chatsPath}/${chatId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },

    getChat(chatId: number) {
      return api.fetchForAccount<{ chat: HermesChat }>(
        accountId,
        `${chatsPath}/${chatId}`,
      );
    },

    listMessages(chatId: number) {
      return api.fetchForAccount<{ messages: HermesMessage[] }>(
        accountId,
        `${chatsPath}/${chatId}/messages`,
      );
    },

    sendMessage(
      chatId: number,
      content: string,
      clientId?: string,
      skills?: string[],
      kbBindings?: KbWikiBinding[],
      extraBindings?: {
        materials_bindings?: unknown[];
        journey_bindings?: unknown[];
        brand_site_bindings?: unknown[];
      },
    ) {
      return api.fetchForAccount<{ message: HermesMessage }>(
        accountId,
        `${chatsPath}/${chatId}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            content,
            ...(clientId ? { client_id: clientId } : {}),
            ...(skills ? { skills } : {}),
            ...(kbBindings ? { kb_bindings: kbBindings } : {}),
            // 显式空数组表示「不选」，必须带上，不能靠省略保留旧绑定。
            ...(extraBindings?.materials_bindings !== undefined
              ? { materials_bindings: extraBindings.materials_bindings }
              : {}),
            ...(extraBindings?.journey_bindings !== undefined
              ? { journey_bindings: extraBindings.journey_bindings }
              : {}),
            ...(extraBindings?.brand_site_bindings !== undefined
              ? { brand_site_bindings: extraBindings.brand_site_bindings }
              : {}),
          }),
        },
      );
    },

    /** Re-run stream for the user turn behind a failed/cancelled assistant — no duplicate user bubble. */
    retryMessage(chatId: number, assistantMessageId: number) {
      return api.fetchForAccount<{ deleted_message_id: number; user_message_id: number }>(
        accountId,
        `${chatsPath}/${chatId}/messages/${assistantMessageId}/retry`,
        { method: "POST" },
      );
    },

    /**
     * Cold-start / catch-up only. Live transport is ActionCable (HermesStreamChannel).
     * Kept for reconnect gap-fill and optional test fallback — not the primary path.
     */
    pollStream(chatId: number, afterId?: number) {
      const query = afterId ? `?after_id=${afterId}` : "";
      return api.fetchForAccount<{ events: StreamEvent[]; messages: HermesMessage[] }>(
        accountId,
        `${chatsPath}/${chatId}/stream${query}`,
      );
    },

    getProposal(proposalId: number) {
      return api.fetchForAccount<{ proposal: HermesWriteProposal }>(
        accountId,
        `/api/v1/hermes/write_proposals/${proposalId}`,
      );
    },

    getHtmlPreview(previewId: number) {
      return withHtmlPreviewFetchLimit(() =>
        api.fetchForAccount<{ preview: HermesHtmlPreviewDetail }>(
          accountId,
          `/api/v1/hermes/html_previews/${previewId}`,
        ),
      );
    },

    applyHtmlPreview(previewId: number) {
      return api.fetchForAccount<{
        page: {
          id?: number;
          slug?: string;
          title?: string;
          html_content?: string;
          is_home?: boolean;
        };
        preview: HermesHtmlPreviewDetail;
        hint?: string;
      }>(accountId, `/api/v1/hermes/html_previews/${previewId}/apply`, { method: "POST" });
    },

    acceptProposal(proposalId: number, body?: { name?: string }) {
      return api.fetchForAccount<{ proposal: HermesWriteProposal }>(
        accountId,
        `/api/v1/hermes/write_proposals/${proposalId}/accept`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body ?? {}),
        },
      );
    },

    rejectProposal(proposalId: number) {
      return api.fetchForAccount<{ proposal: HermesWriteProposal }>(
        accountId,
        `/api/v1/hermes/write_proposals/${proposalId}/reject`,
        { method: "POST" },
      );
    },

    getScoreOptimizePlan(chatId: number) {
      return api.fetchForAccount<HermesScoreOptimizePlanState>(
        accountId,
        `${chatsPath}/${chatId}/score_optimize_plan`,
      );
    },

    confirmScoreOptimizePlan(chatId: number, planId: string) {
      return api.fetchForAccount<HermesScoreOptimizePlanState>(
        accountId,
        `${chatsPath}/${chatId}/score_optimize_plan/confirm`,
        { method: "POST", body: JSON.stringify({ plan_id: planId }) },
      );
    },

    rejectScoreOptimizePlan(chatId: number, planId: string) {
      return api.fetchForAccount<HermesScoreOptimizePlanState>(
        accountId,
        `${chatsPath}/${chatId}/score_optimize_plan/reject`,
        { method: "POST", body: JSON.stringify({ plan_id: planId }) },
      );
    },

    listDbSkills(scope: HermesSkillScope) {
      return api.fetchForAccount<{ skills: HermesDbSkill[] }>(
        accountId,
        `${skillsPath}?scope=${scope}`,
      );
    },

    importSkillUrl(url: string) {
      return api.fetchForAccount<HermesSkillImportDraft>(accountId, `${skillsPath}/import_url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
    },

    createDbSkill(payload: HermesSkillCreatePayload) {
      return api.fetchForAccount<{ skill: HermesDbSkill }>(accountId, skillsPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },

    getDbSkill(skillId: number) {
      return api.fetchForAccount<{ skill: HermesDbSkillDetail }>(
        accountId,
        `${skillsPath}/${skillId}`,
      );
    },

    getDbSkillFile(skillId: number, path: string) {
      const filePath = encodeHermesSkillFilePath(path);
      return api.fetchForAccount<{ path: string; body: string }>(
        accountId,
        `${skillsPath}/${skillId}/files/${filePath}`,
      );
    },

    updateDbSkillPackage(skillId: number, payload: HermesSkillPackageUpdatePayload) {
      return api.fetchForAccount<{ skill: HermesDbSkill }>(
        accountId,
        `${skillsPath}/${skillId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },

    restoreDbSkill(skillId: number) {
      return api.fetchForAccount<{ skill: HermesDbSkill }>(
        accountId,
        `${skillsPath}/${skillId}/restore`,
        { method: "POST" },
      );
    },

    diffDbSkillOrigin(skillId: number) {
      return api.fetchForAccount<{ diffs: HermesSkillOriginDiffEntry[] }>(
        accountId,
        `${skillsPath}/${skillId}/diff_origin`,
      );
    },

    /** @deprecated 后端 install 已 410；请用 importSkillUrl + createDbSkill */
    installDbSkill(_url: string, _target: HermesSkillInstallTarget): never {
      throw new Error("installDbSkill 已废弃，请使用 importSkillUrl 与 createDbSkill");
    },

    deleteDbSkill(skillId: number) {
      return api.fetchForAccount<{ skill: HermesDbSkill }>(
        accountId,
        `${skillsPath}/${skillId}`,
        { method: "DELETE" },
      );
    },
  };
}

/** Stream is complete only on terminal events — not on first assistant row. */
export function isHermesStreamComplete(events: StreamEvent[]): boolean {
  return events.some((event) => isHermesTerminalEvent(event));
}

export function isHermesTerminalEvent(event: StreamEvent): boolean {
  return event.type === "done" || event.type === "error" || event.type === "cancelled";
}

/**
 * 仅当终态事件属于当前待回复轮次时才解锁发送态。
 * pending 未知时拒绝解锁，避免 catch-up 读到旧轮 done 导致输入区闪烁。
 */
export function hermesTerminalEventUnlocksSend(
  event: StreamEvent,
  pendingUserMessageId: number | null,
): boolean {
  if (!isHermesTerminalEvent(event)) return false;
  if (pendingUserMessageId == null) return false;

  const terminalUserMessageId =
    typeof event.user_message_id === "number" ? event.user_message_id : null;
  if (terminalUserMessageId != null && terminalUserMessageId !== pendingUserMessageId) {
    return false;
  }
  return true;
}

export function hermesStreamEventsUnlockSend(
  events: StreamEvent[],
  pendingUserMessageId: number | null,
): boolean {
  return events.some((event) => hermesTerminalEventUnlocksSend(event, pendingUserMessageId));
}

/** OpenCode-parity web tool names; legacy aliases accepted during transition. */
export function isWebfetchToolName(name: string | null | undefined): boolean {
  return name === "webfetch" || name === "fetch_url";
}

export function isWebsearchToolName(name: string | null | undefined): boolean {
  return name === "websearch" || name === "web_search";
}

/** Format byte count for webfetch tool chips (matches Autofill KB / Ruby summary). */
export function formatFetchUrlBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(Number(bytes))) return "";
  const n = Number(bytes);
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

/** Hostname (or short fallback) for webfetch status lines. */
export function fetchUrlHostLabel(url: string | null | undefined): string {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  try {
    return new URL(trimmed).hostname || "";
  } catch {
    return trimmed.replace(/^https?:\/\//i, "").split("/")[0] || "";
  }
}

export function isFetchUrlToolFailure(meta: {
  error?: string | null;
  bytes?: number | null;
}): boolean {
  if (typeof meta.error === "string" && meta.error.trim() !== "") return true;
  // Failed fetches persist bytes: 0 (+ error). Avoid showing "0 B" spam when error is missing.
  return meta.bytes === 0;
}

/** Compact status line (e.g. `www.example.com · 2.0 KB · 已截断` / `example.com · 抓取失败`). */
export function formatFetchUrlToolStatus(meta: {
  bytes?: number | null;
  truncated?: boolean;
  final_url?: string | null;
  error?: string | null;
}): string {
  const label = fetchUrlHostLabel(meta.final_url) || "webfetch";
  if (isFetchUrlToolFailure(meta)) {
    return `${label} · 抓取失败`;
  }
  const size = formatFetchUrlBytes(meta.bytes);
  const parts = [label];
  if (size) parts.push(size);
  if (meta.truncated) parts.push("已截断");
  return parts.join(" · ");
}

/** Drop consecutive identical fetch failures for the same URL. */
export function dedupeFetchUrlToolResults(
  results: HermesToolResultMeta[],
): HermesToolResultMeta[] {
  const out: HermesToolResultMeta[] = [];
  for (const result of results) {
    const prev = out[out.length - 1];
    if (
      prev &&
      isFetchUrlToolFailure(prev) &&
      isFetchUrlToolFailure(result) &&
      (prev.final_url || "") === (result.final_url || "")
    ) {
      continue;
    }
    out.push(result);
  }
  return out;
}

export function fetchUrlResultsFromPayload(
  toolPayload: Record<string, unknown> | null | undefined,
): HermesToolResultMeta[] {
  const raw = toolPayload?.tool_results;
  if (!Array.isArray(raw)) return [];
  const filtered = raw.filter((entry): entry is HermesToolResultMeta => {
    if (!entry || typeof entry !== "object") return false;
    return isWebfetchToolName((entry as HermesToolResultMeta).name);
  });
  return dedupeFetchUrlToolResults(filtered);
}

function coerceProposalId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Pending write proposals on an assistant message (for WriteConfirmCard). */
export function hermesMessageProposalsFromPayload(
  toolPayload: Record<string, unknown> | null | undefined,
): HermesMessageProposal[] {
  if (!toolPayload) return [];

  const rawList = toolPayload.proposals;
  if (Array.isArray(rawList) && rawList.length > 0) {
    const fromList: HermesMessageProposal[] = [];
    for (const entry of rawList) {
      if (!entry || typeof entry !== "object") continue;
      const record = entry as Record<string, unknown>;
      const id = coerceProposalId(record.id);
      if (id == null) continue;
      const status = coerceProposalStatus(record.status);
      fromList.push({
        id,
        action: typeof record.action === "string" ? record.action : undefined,
        payload:
          record.payload && typeof record.payload === "object"
            ? (record.payload as Record<string, unknown>)
            : null,
        ...(status ? { status } : {}),
      });
    }
    if (fromList.length > 0) return fromList;
  }

  const id = coerceProposalId(toolPayload.proposal_id);
  if (id == null) return [];
  const status = coerceProposalStatus(toolPayload.status);
  return [
    {
      id,
      action: typeof toolPayload.action === "string" ? toolPayload.action : undefined,
      payload:
        toolPayload.payload && typeof toolPayload.payload === "object"
          ? (toolPayload.payload as Record<string, unknown>)
          : null,
      ...(status ? { status } : {}),
    },
  ];
}

/** 从助手消息中提取可读性优化待确认计划。 */
export function hermesMessageScoreOptimizePlansFromPayload(
  toolPayload: Record<string, unknown> | null | undefined,
): HermesScoreOptimizePlanCard[] {
  const raw = toolPayload?.tool_results;
  if (!Array.isArray(raw)) return [];

  const cards: HermesScoreOptimizePlanCard[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    if (record.name !== "brand_site_propose_score_plan") continue;
    if (record.status !== "pending_confirmation" || !Array.isArray(record.plan)) continue;
    const planId = typeof record.plan_id === "string" ? record.plan_id.trim() : "";
    if (!planId) continue;

    const plan: HermesScoreOptimizePlanItem[] = [];
    let malformed = false;
    for (const item of record.plan) {
      if (!item || typeof item !== "object") {
        malformed = true;
        break;
      }
      const row = item as Record<string, unknown>;
      const slug = typeof row.slug === "string" ? row.slug.trim() : "";
      const action = typeof row.action === "string" ? row.action.trim() : "";
      if (!slug || !action) {
        malformed = true;
        break;
      }
      const expect = typeof row.expect === "string" ? row.expect.trim() : "";
      plan.push({ slug, action, ...(expect ? { expect } : {}) });
    }
    if (!malformed && plan.length > 0) {
      cards.push({ status: "pending_confirmation", plan_id: planId, plan });
    }
  }
  return cards;
}

/** HTML 预览摘要（助手 tool_payload.html_previews，不含全文）。 */
export function hermesMessageHtmlPreviewsFromPayload(
  toolPayload: Record<string, unknown> | null | undefined,
): HermesHtmlPreviewSummary[] {
  if (!toolPayload) return [];

  const rawList = toolPayload.html_previews;
  if (!Array.isArray(rawList) || rawList.length === 0) return [];

  const previews: HermesHtmlPreviewSummary[] = [];
  for (const entry of rawList) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const id = coerceProposalId(record.id);
    if (id == null) continue;
    if (typeof record.slug !== "string" || record.slug.trim() === "") continue;

    const preview: HermesHtmlPreviewSummary = {
      id,
      slug: record.slug,
    };
    if (record.title === null || typeof record.title === "string") {
      preview.title = record.title;
    }
    if (typeof record.byte_size === "number" && Number.isFinite(record.byte_size)) {
      preview.byte_size = record.byte_size;
    }
    previews.push(preview);
  }
  return previews;
}

/** 流式 tool_call 状态条：产品中文，禁止回显英文工具名。未知 name → 正在处理… */
const TOOL_STATUS_ZH: Record<string, string> = {
  materials_list: "正在查阅资源库…",
  materials_get: "正在查看素材…",
  kb_read: "正在查阅知识库…",
  kb_read_pages: "正在查阅知识库…",
  kb_list: "正在查阅知识库…",
  kb_glob: "正在查阅知识库…",
  kb_grep: "正在查阅知识库…",
  kb_stat: "正在查阅知识库…",
  kb_wiki_maintain: "正在整理知识库…",
  kb_propose_seed_update: "正在准备知识库变更…",
  kb_propose_create_brand: "正在准备品牌提案…",
  kb_propose_page_upsert: "正在准备知识库变更…",
  kb_write: "正在更新知识库…",
  kb_edit: "正在更新知识库…",
  kb_rm: "正在整理知识库…",
  kb_ingest_attachment: "正在收录附件…",
  webfetch: "正在打开公开网页…",
  fetch_url: "正在打开公开网页…",
  websearch: "正在搜索公开网页…",
  journey_read_current: "正在查阅客户旅程…",
  journey_propose_upsert: "正在准备旅程变更…",
  brand_site_execute_edit: "正在改站草稿…",
  brand_site_list_pages: "正在查阅官网目录…",
  brand_site_read_page: "正在查阅官网…",
  brand_site_score_draft: "正在预评官网草稿…",
  brand_site_propose_score_plan: "正在准备可读性优化计划…",
  brand_site_confirm_score_plan: "正在确认可读性优化计划…",
  brand_site_write_page: "正在写入官网…",
  brand_site_delete_page: "正在删除官网页面…",
  brand_site_write_collection: "正在更新栏目模板…",
  brand_site_write_item: "正在写入栏目文章…",
  brand_site_delete_item: "正在删除栏目文章…",
  brand_site_list_releases: "正在查阅官网正式版…",
  brand_site_read_release_page: "正在查阅官网正式版…",
  brand_site_preview_html: "正在生成预览…",
  brand_site_apply_preview: "正在写入官网草稿…",
  brand_site_confirm_edit_plan: "正在确认改页计划…",
  brand_site_confirm_clone_plan: "正在确认复刻计划…",
  brand_site_read_brief: "正在查阅官网意图…",
  brand_site_update_brief: "正在更新官网意图…",
  brand_site_propose_release_rollback: "正在准备官网回滚…",
  brand_site_list_assets: "正在查阅官网配图…",
  brand_site_reference_material: "正在引用素材…",
  brand_site_read_platform_template: "正在查阅平台模板…",
  hermes_ask_choices: "正在确认选项…",
  hermes_lucide_icon: "正在挑选图标…",
  hermes_html_scan_emoji: "正在检查图标…",
  skills_list: "正在查看技能…",
  skill_view: "正在查看技能…",
};

/** 官网 slug → 商家可读名（公共部分 / 首页 / 路径） */
export function hermesBrandSiteTargetLabel(slug: string | null | undefined): string | null {
  const s = slug?.trim();
  if (!s) return null;
  if (s === "_header") return "页头";
  if (s === "_footer") return "页脚";
  if (s === "_styles") return "全站样式";
  if (s === "_nav") return "导航";
  if (s === "index" || s === "/") return "首页";
  if (s.startsWith("_")) return s.slice(1) || s;
  return s.startsWith("/") ? s : `/${s}`;
}

function appendTarget(base: string, target: string | null | undefined): string {
  const t = target?.trim();
  if (!t) return base.endsWith("…") ? base : `${base}…`;
  const stem = base.replace(/…\s*$/, "");
  return `${stem} · ${t}…`;
}

/** 把 tool_call 编成状态条文案；可带 slug/key 等目标。 */
export function formatToolCallStatus(
  toolName: string,
  target?: {
    slug?: string | null;
    key?: string | null;
    collection_key?: string | null;
    query?: string | null;
    public_id?: string | null;
  },
): string {
  const name = toolName.trim();
  const base = TOOL_STATUS_ZH[name] ?? "正在调用工具…";

  if (name.startsWith("brand_site_")) {
    const pageLabel = hermesBrandSiteTargetLabel(target?.slug ?? undefined);
    const collectionLabel =
      target?.key?.trim() ||
      target?.collection_key?.trim() ||
      null;
    if (
      name === "brand_site_write_page" ||
      name === "brand_site_read_page" ||
      name === "brand_site_delete_page" ||
      name === "brand_site_preview_html"
    ) {
      return appendTarget(base, pageLabel);
    }
    if (name === "brand_site_write_collection") {
      return appendTarget(base, collectionLabel ? `/${collectionLabel}` : null);
    }
    if (name === "brand_site_write_item" || name === "brand_site_delete_item") {
      const label =
        pageLabel || (collectionLabel ? `/${collectionLabel}` : null);
      return appendTarget(base, label);
    }
    return base;
  }

  if (name === "materials_list" && target?.query?.trim()) {
    return appendTarget("正在搜索资源库", target.query.trim().slice(0, 24));
  }
  if (name === "materials_get" && target?.public_id?.trim()) {
    return appendTarget(base, target.public_id.trim().slice(0, 16));
  }
  if (name === "websearch" && target?.query?.trim()) {
    return appendTarget("正在搜索", target.query.trim().slice(0, 24));
  }

  return base;
}

export function statusFromStreamEvent(event: StreamEvent): string | null {
  if (event.type === "context_compacting") {
    return "正在总结上下文…";
  }
  if (event.type === "brand_site_authoring") {
    if (event.status === "started") return "正在改站草稿…";
    if (event.status === "error") return event.error?.trim() || "改站失败";
    return null;
  }
  if (event.type === "karpathy_wiki") {
    if (event.status === "started") return "正在整理知识库…";
    if (event.status === "error") return event.error?.trim() || "知识库维护失败";
    return null;
  }
  if (event.type === "tool_call" && event.delta?.trim()) {
    return formatToolCallStatus(event.delta, {
      slug: event.slug,
      key: event.key,
      collection_key: event.collection_key,
      query: event.query,
      public_id: event.public_id,
    });
  }
  if (event.type === "tool_result" && isWebfetchToolName(event.delta)) {
    return formatFetchUrlToolStatus({
      bytes: event.bytes,
      truncated: event.truncated === true,
      final_url: event.final_url,
      error: event.error,
    });
  }
  return null;
}

/** 站页草稿被 Hermes 工具改写后，预览应刷新（非 list）。 */
export function isBrandSiteDraftMutatingToolName(name: string | null | undefined): boolean {
  const n = name?.trim();
  return (
    n === "brand_site_write_page" ||
    n === "brand_site_delete_page" ||
    n === "brand_site_apply_preview" ||
    n === "brand_site_write_item" ||
    n === "brand_site_write_collection" ||
    n === "brand_site_delete_item"
  );
}

const HERMES_STREAM_TERMINAL_STATUS = new Set(["done", "error", "cancelled"]);
/** 无 stream_status 的旧消息：仅最近更新的才可能是进行中。 */
const HERMES_IN_FLIGHT_FRESH_MS = 15 * 60 * 1000;
/**
 * 末条仍是 user（尚无 assistant shell）时：超过此时长仍无回复，视为任务未启动/worker 挂了，
 * 不再无限转圈（否则 jobs 挂掉后刷新会话会一直「跳动」等待）。
 */
const HERMES_ORPHAN_USER_FRESH_MS = 3 * 60 * 1000;

function lastVisibleHermesMessage(messages: HermesMessage[]): HermesMessage | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === "user" || message?.role === "assistant") return message;
  }
  return null;
}

function sourceRefUserMessageId(message: HermesMessage): number | null {
  const raw = message.tool_payload?.source_ref;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * 刷新 / 切回会话后：这一轮是否仍在生成（需恢复「思考中」）。
 * 以 assistant.tool_payload.stream_status 为准；缺省时仅当消息刚更新过才当作进行中。
 */
export function hermesInFlightTurn(messages: HermesMessage[]): {
  sending: boolean;
  userMessageId: number | null;
} {
  const last = lastVisibleHermesMessage(messages);
  if (!last) return { sending: false, userMessageId: null };

  if (last.role === "user") {
    const stamped = Date.parse(last.updated_at || last.created_at);
    if (Number.isFinite(stamped) && Date.now() - stamped > HERMES_ORPHAN_USER_FRESH_MS) {
      return { sending: false, userMessageId: null };
    }
    return { sending: true, userMessageId: last.id };
  }

  const status = String(last.tool_payload?.stream_status ?? "");
  if (HERMES_STREAM_TERMINAL_STATUS.has(status)) {
    return { sending: false, userMessageId: null };
  }
  if (isHermesAssistantRetryable(last)) {
    return { sending: false, userMessageId: null };
  }
  const proposals = hermesMessageProposalsFromPayload(last.tool_payload);
  if (proposals.some((proposal) => proposal.status == null || proposal.status === "pending")) {
    return { sending: false, userMessageId: null };
  }

  if (status !== "streaming") {
    const updated = Date.parse(last.updated_at);
    if (!Number.isFinite(updated) || Date.now() - updated > HERMES_IN_FLIGHT_FRESH_MS) {
      return { sending: false, userMessageId: null };
    }
  }

  const lastUser = [...messages].reverse().find((message) => message.role === "user");
  return {
    sending: true,
    userMessageId: sourceRefUserMessageId(last) ?? lastUser?.id ?? null,
  };
}

/** Failed/cancelled assistant turns that expose a Retry action in the drawer. */
export function isHermesAssistantRetryable(message: HermesMessage): boolean {
  if (message.role !== "assistant") return false;
  const payload = message.tool_payload;
  if (!payload || typeof payload !== "object") return false;
  if (payload.error != null && String(payload.error).length > 0) return true;
  return payload.cancelled === true;
}

export function removeHermesMessage(
  current: HermesMessage[],
  messageId: number,
): HermesMessage[] {
  return current.filter((message) => message.id !== messageId);
}

/** Drop a user turn and any assistant replies tied via tool_payload.source_ref. */
export function removeHermesTurn(
  current: HermesMessage[],
  userMessageId: number,
): HermesMessage[] {
  const ref = String(userMessageId);
  return current.filter((message) => {
    if (message.id === userMessageId) return false;
    if (message.role === "assistant") {
      const sourceRef = message.tool_payload?.source_ref;
      if (sourceRef != null && String(sourceRef) === ref) return false;
    }
    return true;
  });
}

/** Upsert stream messages into the conversation (content grows while streaming).
 * Dedupes by id; also replaces optimistic temps that share client_id. */
export function mergeHermesMessages(
  current: HermesMessage[],
  incoming: HermesMessage[],
): HermesMessage[] {
  const byId = new Map(current.map((message) => [message.id, message]));
  const idByClientId = new Map<string, number>();
  for (const message of current) {
    if (message.client_id) {
      idByClientId.set(message.client_id, message.id);
    }
  }

  for (const message of incoming) {
    if (message.client_id) {
      const previousId = idByClientId.get(message.client_id);
      if (previousId !== undefined && previousId !== message.id) {
        byId.delete(previousId);
      }
      idByClientId.set(message.client_id, message.id);
    }
    byId.set(message.id, message);
  }
  return Array.from(byId.values()).sort((a, b) => a.id - b.id);
}

function hermesAssistantShell(messageId: number, patch: Partial<HermesMessage>): HermesMessage {
  const now = new Date().toISOString();
  return {
    id: messageId,
    role: "assistant",
    content: "",
    tool_payload: {},
    created_at: now,
    updated_at: now,
    ...patch,
  };
}

/**
 * Live Cable path: append a single text/thinking delta onto the matching assistant row.
 * Creates a shell message when the first delta arrives before any snapshot.
 */
export function applyHermesStreamDelta(
  current: HermesMessage[],
  event: StreamEvent,
): HermesMessage[] {
  if (event.type !== "text_delta" && event.type !== "thinking_delta") return current;
  const messageId = event.message_id;
  if (typeof messageId !== "number" || !event.delta) return current;

  const existing = current.find((message) => message.id === messageId);
  if (!existing) {
    if (event.type === "text_delta") {
      return mergeHermesMessages(current, [
        hermesAssistantShell(messageId, { content: event.delta }),
      ]);
    }
    return mergeHermesMessages(current, [
      hermesAssistantShell(messageId, {
        tool_payload: { thinking: event.delta },
      }),
    ]);
  }

  if (event.type === "text_delta") {
    return current.map((message) =>
      message.id === messageId
        ? { ...message, content: `${message.content ?? ""}${event.delta}` }
        : message,
    );
  }

  const prevThinking =
    typeof existing.tool_payload?.thinking === "string" ? existing.tool_payload.thinking : "";
  return current.map((message) =>
    message.id === messageId
      ? {
          ...message,
          tool_payload: {
            ...(message.tool_payload ?? {}),
            thinking: `${prevThinking}${event.delta}`,
          },
        }
      : message,
  );
}

/**
 * Catch-up / reconnect: merge DB snapshots, then fold buffered deltas as cumulative text
 * (replace when longer) so throttled DB rows do not lag behind StreamBuffer.
 *
 * 若事件流已含 done/error/cancelled：以服务端 snapshot（as_api_json，经 MarkdownNormalizer）
 * 全量为准，不再用 raw text_delta 覆盖，避免完成态规范化被流式拼接冲掉。
 */
export function foldHermesStreamCatchUp(
  current: HermesMessage[],
  snapshots: HermesMessage[],
  events: StreamEvent[],
): HermesMessage[] {
  let next = snapshots.length > 0 ? mergeHermesMessages(current, snapshots) : current;

  if (events.some((event) => isHermesTerminalEvent(event))) {
    return next;
  }

  const textById = new Map<number, string>();
  const thinkingById = new Map<number, string>();

  for (const event of events) {
    if (typeof event.message_id !== "number" || !event.delta) continue;
    if (event.type === "text_delta") {
      textById.set(event.message_id, `${textById.get(event.message_id) ?? ""}${event.delta}`);
    } else if (event.type === "thinking_delta") {
      thinkingById.set(
        event.message_id,
        `${thinkingById.get(event.message_id) ?? ""}${event.delta}`,
      );
    }
  }

  for (const [messageId, text] of textById) {
    const existing = next.find((message) => message.id === messageId);
    if (!existing) {
      next = mergeHermesMessages(next, [hermesAssistantShell(messageId, { content: text })]);
      continue;
    }
    if (text.length >= (existing.content?.length ?? 0)) {
      next = next.map((message) =>
        message.id === messageId ? { ...message, content: text } : message,
      );
    }
  }

  for (const [messageId, thinking] of thinkingById) {
    const existing = next.find((message) => message.id === messageId);
    if (!existing) {
      next = mergeHermesMessages(next, [
        hermesAssistantShell(messageId, { tool_payload: { thinking } }),
      ]);
      continue;
    }
    const prev =
      typeof existing.tool_payload?.thinking === "string" ? existing.tool_payload.thinking : "";
    if (thinking.length >= prev.length) {
      next = next.map((message) =>
        message.id === messageId
          ? {
              ...message,
              tool_payload: { ...(message.tool_payload ?? {}), thinking },
            }
          : message,
      );
    }
  }

  return next;
}

export function parseHermesCablePayload(data: unknown): HermesCablePayload | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as Record<string, unknown>;
  if (typeof record.account_id !== "number" || typeof record.chat_id !== "number") return null;
  if (typeof record.event !== "object" || record.event === null) return null;
  const event = record.event as StreamEvent;
  if (typeof event.type !== "string") return null;

  return {
    account_id: record.account_id,
    chat_id: record.chat_id,
    event,
    message: (record.message as HermesMessage | null | undefined) ?? null,
  };
}

/**
 * @deprecated Prefer ActionCable (HermesStreamChannel). Kept for catch-up / tests only.
 * Does not bound long Agent jobs — Cable is the correctness path.
 */
export async function waitForAssistantReply(
  accountId: string,
  chatId: number,
  userMessageId: number,
  onProgress?: (progress: HermesStreamProgress) => void,
  options?: { intervalMs?: number; maxAttempts?: number },
): Promise<HermesMessage[]> {
  const hermes = createHermesApi(accountId);
  const intervalMs = options?.intervalMs ?? 500;
  const maxAttempts = options?.maxAttempts ?? 40;
  let lastFingerprint = "";
  let lastStatus: string | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const { events, messages } = await hermes.pollStream(chatId, userMessageId);
    const statusEvent = [...events]
      .reverse()
      .find(
        (event) =>
          event.type === "tool_result" ||
          event.type === "tool_call" ||
          event.type === "context_compacting" ||
          event.type === "karpathy_wiki",
      );
    const status = statusEvent ? statusFromStreamEvent(statusEvent) : null;
    const fingerprint = messages
      .map((message) => `${message.id}:${message.content.length}:${message.updated_at}`)
      .join("|");

    if (fingerprint !== lastFingerprint || status !== lastStatus) {
      lastFingerprint = fingerprint;
      lastStatus = status;
      onProgress?.({ messages, status });
    }

    if (isHermesStreamComplete(events)) {
      return messages;
    }

    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
  }

  const { messages } = await hermes.pollStream(chatId, userMessageId);
  onProgress?.({ messages, status: null });
  return messages;
}
